import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import moment from 'moment';
import { get } from 'lodash';
import AdminActivityTable from './AdminActivityTable';
import DateAndGranularityPicker, {
  dateRangeOptionsList,
} from '../../../../components/DateAndGranularityPicker/DateAndGranularityPicker';
import useActiveUser from '../../../../hooks/useActiveUser';
import { setUserTableOptions } from '../../../../actions/tableOptions';
import OverlayLoader from '../../../../components/OverlayLoader';
import { fetchUserLogs } from '../../../../actions/logs';
import { fetchUsers } from '../../../../actions/users';
import { getUserFullName } from '../../../../helpers/user';

const TABLE_ID = '#admin/activity-table';

const dateRangeOptions = {
  allHistory: dateRangeOptionsList.allHistory,
  today: dateRangeOptionsList.today,
  thisWeek: dateRangeOptionsList.thisWeek,
  thisMonth: dateRangeOptionsList.thisMonth,
  thisQuarter: dateRangeOptionsList.thisQuarter,
  thisYear: dateRangeOptionsList.thisYear,
};

const activityTypes = {
  RESCUE_CLAIM: 'RESCUE_CLAIM',
  RESCUE_ADOPT: 'RESCUE_ADOPT',
  RESCUE_UNADOPT: 'RESCUE_UNADOPT',
  USER_MODIFIED: 'USER_MODIFIED',
  USER_ROLE_ATTACH: 'USER_ROLE_ATTACH',
  USER_ROLE_DETACH: 'USER_ROLE_DETACH',
  RECEIVING_AGENCY_CREATED: 'RECEIVING_AGENCY_CREATED',
  RECEIVING_AGENCY_MODIFIED: 'RECEIVING_AGENCY_MODIFIED',
  DONOR_CREATED: 'DONOR_CREATED',
  DONOR_MODIFIED: 'DONOR_MODIFIED',
  DONOR_DELETED: 'DONOR_DELETED',
  DONATION_MODIFIED: 'DONATION_MODIFIED',
  MARKETING_HUB_FEED_ENGAGED: 'MARKETING_HUB_FEED_ENGAGED',
  MARKETING_HUB_ACCOUNT_LINKED: 'MARKETING_HUB_ACCOUNT_LINKED',
  USER_LOGGED_IN: 'USER_LOGGED_IN',
};

const AdminDashboardActivity = () => {
  const dispatch = useDispatch();
  const activeUser = useActiveUser();
  const logsEntities = useSelector((state) => state.entities.logs);
  const logs = Object.values(logsEntities.byId);
  const usersEntities = useSelector((state) => state.entities.users);
  const usersList = Object.values(usersEntities.byId);
  const tableOptions = useSelector((state) => state.tableOptions);
  const [dateRange, setDateRange] = useState({});
  const mappedLogs = logs.map((log) => {
    const user = usersList.find((u) => u.id === log.user_id);

    if (user) {
      return { ...log, user: getUserFullName(user) };
    }

    return log;
  });

  const filteredLogs = mappedLogs.filter((log) =>
    moment(log.created_at).isBetween(
      moment(dateRange.from).format('YYYYMMDD'),
      moment(dateRange.to).format('YYYYMMDD'),
      undefined,
      []
    )
  );

  useEffect(() => {
    dispatch(fetchUserLogs());
    dispatch(fetchUsers());
  }, []);

  const getTableSelectedPeriod = () => {
    const previousTableState = get(tableOptions, [activeUser.id, TABLE_ID], null);
    return get(previousTableState, ['period'], dateRangeOptions.thisMonth.value);
  };

  const getTableSelectedDateRange = () => {
    const previousTableState = get(tableOptions, [activeUser.id, TABLE_ID], null);
    return get(previousTableState, ['dateRange'], dateRangeOptions.thisMonth.value);
  };

  const getActionTypes = () => {
    const previousTableState = get(tableOptions, [activeUser.id, TABLE_ID], null);
    const columnIndex = get(previousTableState, 'columns', []).findIndex((c) => c.name === 'action_type');
    const value = get(previousTableState, ['filterList', columnIndex], []);

    return value.length > 0 ? value : ['All'];
  };

  const onActionTypesChange = (currentTarget) => {
    const { dataset } = currentTarget;
    const choiceName = dataset.value;

    const previousTableState = get(tableOptions, [activeUser.id, TABLE_ID], null);
    const columnIndex = get(previousTableState, 'columns', []).findIndex((c) => c.name === 'action_type');
    const previousFilters = get(previousTableState, 'filterList', []);

    if (columnIndex < 0 || previousFilters.length === 0) {
      return null;
    }

    if (choiceName === 'all') {
      previousFilters[columnIndex] = [];
      return dispatch(
        setUserTableOptions(activeUser.id, TABLE_ID, {
          ...previousTableState,
          filterList: previousFilters,
        })
      );
    }

    previousFilters[columnIndex] = previousFilters[columnIndex].includes(choiceName)
      ? previousFilters[columnIndex].filter((row) => row !== choiceName)
      : [...previousFilters[columnIndex], choiceName];

    return dispatch(
      setUserTableOptions(activeUser.id, TABLE_ID, {
        ...previousTableState,
        filterList: previousFilters,
      })
    );
  };

  const onDateFilterChange = (from, to, period) => {
    setDateRange({ from, to });
    const previousTableState = get(tableOptions, [activeUser.id, TABLE_ID], null);

    dispatch(
      setUserTableOptions(activeUser.id, TABLE_ID, {
        ...previousTableState,
        period,
        dateRange: {
          from,
          to,
        },
      })
    );
  };

  const selectedPeriod = getTableSelectedPeriod();
  const selectedDateRange = getTableSelectedDateRange();
  const actionTypes = getActionTypes();

  return (
    <OverlayLoader isLoading={logsEntities.inflight || usersEntities.inflight}>
      <Box my={1} p={2} bgcolor="background.paper" boxShadow={25}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
          <Grid item>
            <FormControl variant="outlined" size="small" sx={{ margin: 1, minWidth: 140 }}>
              <InputLabel>Types</InputLabel>
              <Select
                variant="outlined"
                label="Types"
                multiple
                value={actionTypes}
                onChange={(event) => onActionTypesChange(event.currentTarget)}
                renderValue={(value) => {
                  if (value.length > 1) {
                    return `${value.length} selected`;
                  }
                  return value.join(', ');
                }}
              >
                <MenuItem value="all" data-name="all">
                  All
                </MenuItem>
                {Object.values(activityTypes).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            <DateAndGranularityPicker
              showCustomDateRange
              showGranularity={false}
              dateRangeOptions={dateRangeOptions}
              defaultDateRange={selectedPeriod}
              customDateRangeOptions={{
                maxDate: moment().toDate(),
              }}
              customDateRangeValue={{
                startDate: selectedDateRange.from,
                endDate: selectedDateRange.to,
              }}
              onChange={(data) =>
                onDateFilterChange(moment(data.dateRange.startDate), moment(data.dateRange.endDate), data.period)
              }
            />
          </Grid>
        </Grid>
      </Box>
      <div>
        <AdminActivityTable tableId={TABLE_ID} activities={filteredLogs} activityTypes={activityTypes} />
      </div>
    </OverlayLoader>
  );
};

export default AdminDashboardActivity;
